import { GetCategoryTreeQuery } from '@/graphql/types/generated';

import { MarketsCoverageCell } from './MarketsCoverageCell';

export type TreeCategory = GetCategoryTreeQuery['categories'][0];

type Market = { id: string; name: string };

export type CategoryRow = {
  id: string;
  name: string;
  // Indentation only. A stocked subcategory is listed even when its parent was
  // dropped, so this never promises the row above is the parent.
  depth: number;
  activeWarehouses: Market[];
  bannerWarehouses: Market[];
  // Whether the markets missing from bannerWarehouses are covered by the
  // shared default rather than genuinely having nothing.
  hasBannerDefaultCreative: boolean;
};

const byPosition = <T extends { position?: number | null; name: string }>(
  a: T,
  b: T,
) => (a.position ?? 0) - (b.position ?? 0) || a.name.localeCompare(b.name);

const toRow = (
  category: Omit<TreeCategory, 'subcategories'>,
  depth: number,
): CategoryRow => ({
  id: category.id,
  name: category.name,
  depth,
  activeWarehouses: category.activeWarehouses,
  bannerWarehouses: category.bannerWarehouses,
  hasBannerDefaultCreative: category.hasBannerDefaultCreative,
});

/**
 * The category tree flattened into the order the list displays it: each parent
 * followed by its subcategories, both ordered by position.
 *
 * A category stocked in no market is dropped — there is no category page for it
 * to appear on, so there is nothing to put a banner above. Dropping a parent
 * does not drop its stocked subcategories, which is why `depth` is presentation
 * and not structure.
 */
export const toCategoryRows = (categories: TreeCategory[]): CategoryRow[] =>
  categories
    .slice()
    .sort(byPosition)
    .flatMap((category) => {
      const subcategoryRows = (category.subcategories ?? [])
        .slice()
        .sort(byPosition)
        .filter((subcategory) => subcategory.activeWarehouses.length)
        .map((subcategory) => toRow(subcategory, 1));

      return category.activeWarehouses.length
        ? [toRow(category, 0), ...subcategoryRows]
        : subcategoryRows;
    });

/**
 * How the Banner column summarizes a row. `bannerWarehouses` only lists
 * markets that brought their own creative, so an empty list is not "nothing"
 * when the shared default covers the rest — that reads as unset otherwise,
 * which is the discrepancy this exists to close.
 */
export const formatBannerCoverage = (
  row: Pick<
    CategoryRow,
    'id' | 'bannerWarehouses' | 'hasBannerDefaultCreative'
  >,
  allWarehouses: Market[],
) => {
  if (row.bannerWarehouses.length === 0) {
    return row.hasBannerDefaultCreative ? 'Default' : 'Not set';
  }

  return (
    <MarketsCoverageCell
      covered={row.bannerWarehouses}
      allWarehouses={allWarehouses}
      tooltipId={`banner-${row.id}`}
      suffix={row.hasBannerDefaultCreative ? ' + default' : undefined}
    />
  );
};
